const comparisons = [
  {
    topic: 'Language & Communication',
    mills: 'Factory staff only speak Mandarin. Slow replies, misunderstandings on specs.',
    traders: 'Many traders are middlemen with no real steel knowledge. Vague answers.',
    us: 'Fluent English team. We know the specs, the grades, the standards — and we explain everything clearly.',
    icon: 'ri-chat-3-line',
  },
  {
    topic: 'Minimum Order Quantity',
    mills: 'Most mills require 100–500 metric tons minimum. Small buyers get ignored.',
    traders: 'Often inflate minimum quantities to protect their margins.',
    us: 'We work with orders from 25 metric tons. Small or large — every buyer gets the same attention.',
    icon: 'ri-scales-3-line',
  },
  {
    topic: 'Price Transparency',
    mills: 'Prices change daily. No clear breakdown. Hidden port charges.',
    traders: 'Add large markups with no explanation. You never know what you\'re really paying.',
    us: 'Full FOB price breakdown every time — FOB means the price includes getting your steel to the Chinese port, you arrange the ship. No hidden fees.',
    icon: 'ri-price-tag-3-line',
  },
  {
    topic: 'Quality Inspection',
    mills: 'Self-certified only. No independent check. You just trust their word.',
    traders: 'Most skip inspection to save cost. Problems only show up after arrival.',
    us: 'Independent SGS / TUV inspection available on every order. Quality certificates from the mill included with every shipment.',
    icon: 'ri-shield-check-line',
  },
  {
    topic: 'Export Documentation',
    mills: 'Complex paperwork. Mills often unfamiliar with international trade requirements.',
    traders: 'Errors in shipping documents and certificates are common.',
    us: '10+ years of export experience. We handle all documents — Bill of Lading (your proof of shipment), Certificate of Origin, quality test reports, packing list — correctly, every time.',
    icon: 'ri-file-text-line',
  },
  {
    topic: 'After-Sales Support',
    mills: 'Once shipped, you\'re on your own. No recourse for issues.',
    traders: 'Hard to reach after payment. Disputes go unresolved.',
    us: 'Dedicated account manager for every client. We stay with you from inquiry to port arrival.',
    icon: 'ri-customer-service-2-line',
  },
];

const advantages = [
  { icon: 'ri-building-4-line', val: '200+', label: 'Verified Mill Partners' },
  { icon: 'ri-global-line', val: '50+', label: 'Countries Delivered' },
  { icon: 'ri-time-line', val: '2 hrs', label: 'Average Quote Time' },
  { icon: 'ri-repeat-line', val: '85%', label: 'Repeat Order Rate' },
];

export default function WhyUsSection() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-[1420px] mx-auto px-6">

        {/* Header */}
        <div className="text-center mb-14">
          <span className="text-xs font-black tracking-widest uppercase mb-3 block" style={{ color: '#0a2342' }}>
            The Smart Choice
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">
            Why Steel Link Global?
          </h2>
          <p className="text-gray-500 text-sm max-w-2xl mx-auto leading-relaxed">
            Buying steel from China sounds simple — until it isn&apos;t. Here&apos;s how we compare to going direct to a mill or using an unknown trader.
          </p>
        </div>

        {/* Comparison Table — desktop */}
        <div className="hidden md:block rounded-2xl overflow-hidden border border-gray-100 mb-14">
          {/* Table header */}
          <div className="grid grid-cols-4 text-sm font-black">
            <div className="px-6 py-4 bg-gray-50 text-gray-500 uppercase tracking-widest text-xs border-b border-gray-100">
              What Matters
            </div>
            <div className="px-6 py-4 bg-red-50 text-red-700 text-center border-b border-red-100 flex items-center justify-center gap-2">
              <i className="ri-building-2-line" /> Direct from Mill
            </div>
            <div className="px-6 py-4 bg-orange-50 text-orange-700 text-center border-b border-orange-100 flex items-center justify-center gap-2">
              <i className="ri-store-2-line" /> Unknown Trader
            </div>
            <div className="px-6 py-4 text-white text-center border-b flex items-center justify-center gap-2 font-black" style={{ backgroundColor: '#0a2342' }}>
              <i className="ri-award-line" /> Steel Link Global
            </div>
          </div>

          {/* Rows */}
          {comparisons.map((row, i) => (
            <div
              key={row.topic}
              className={`grid grid-cols-4 text-sm ${i < comparisons.length - 1 ? 'border-b border-gray-100' : ''}`}
            >
              {/* Topic */}
              <div className="px-6 py-5 bg-gray-50 flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5" style={{ backgroundColor: 'rgba(10,35,66,0.08)' }}>
                  <i className={`${row.icon} text-sm`} style={{ color: '#0a2342' }} />
                </div>
                <span className="font-bold text-gray-800 text-xs leading-snug pt-1">{row.topic}</span>
              </div>

              {/* Mill */}
              <div className="px-5 py-5 bg-red-50/40 text-gray-600 text-xs leading-relaxed flex items-start gap-2">
                <i className="ri-close-circle-line text-red-400 text-base flex-shrink-0 mt-0.5" />
                <span>{row.mills}</span>
              </div>

              {/* Trader */}
              <div className="px-5 py-5 bg-orange-50/40 text-gray-600 text-xs leading-relaxed flex items-start gap-2">
                <i className="ri-error-warning-line text-orange-400 text-base flex-shrink-0 mt-0.5" />
                <span>{row.traders}</span>
              </div>

              {/* Us */}
              <div className="px-5 py-5 text-xs leading-relaxed flex items-start gap-2" style={{ backgroundColor: 'rgba(10,35,66,0.03)' }}>
                <i className="ri-checkbox-circle-fill text-base flex-shrink-0 mt-0.5" style={{ color: '#0a2342' }} />
                <span className="text-gray-700 font-medium">{row.us}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-5 mb-14">
          {comparisons.map((row) => (
            <div key={row.topic} className="rounded-xl border border-gray-100 overflow-hidden">
              {/* Topic header */}
              <div className="flex items-center gap-3 px-4 py-3" style={{ backgroundColor: '#0a2342' }}>
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/15 flex-shrink-0">
                  <i className={`${row.icon} text-white text-sm`} />
                </div>
                <span className="text-white font-black text-sm">{row.topic}</span>
              </div>
              <div className="divide-y divide-gray-100">
                <div className="flex items-start gap-3 px-4 py-3 bg-red-50/50">
                  <i className="ri-close-circle-line text-red-400 text-base flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs font-bold text-red-600 mb-0.5">Direct from Mill</div>
                    <p className="text-xs text-gray-600 leading-relaxed">{row.mills}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 px-4 py-3 bg-orange-50/50">
                  <i className="ri-error-warning-line text-orange-400 text-base flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs font-bold text-orange-600 mb-0.5">Unknown Trader</div>
                    <p className="text-xs text-gray-600 leading-relaxed">{row.traders}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 px-4 py-3" style={{ backgroundColor: 'rgba(10,35,66,0.04)' }}>
                  <i className="ri-checkbox-circle-fill text-base flex-shrink-0 mt-0.5" style={{ color: '#0a2342' }} />
                  <div>
                    <div className="text-xs font-black mb-0.5" style={{ color: '#0a2342' }}>Steel Link Global</div>
                    <p className="text-xs text-gray-700 leading-relaxed font-medium">{row.us}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom stats + CTA */}
        <div className="rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center gap-8" style={{ backgroundColor: '#0a2342' }}>
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 flex-1 w-full">
            {advantages.map((a) => (
              <div key={a.label} className="text-center">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl mx-auto mb-2" style={{ backgroundColor: 'rgba(126,184,247,0.15)' }}>
                  <i className={`${a.icon} text-lg`} style={{ color: '#7eb8f7' }} />
                </div>
                <div className="text-2xl font-black text-white">{a.val}</div>
                <div className="text-xs font-medium mt-0.5" style={{ color: '#7eb8f7' }}>{a.label}</div>
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="hidden md:block w-px h-24 bg-white/10 flex-shrink-0" />

          {/* CTA */}
          <div className="text-center md:text-left flex-shrink-0">
            <p className="text-white font-black text-lg mb-1">Ready to experience the difference?</p>
            <p className="text-sm mb-5" style={{ color: '#7eb8f7' }}>Get your free FOB quote in under 2 hours.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
              <a
                href="/quote"
                className="px-6 py-3 bg-white font-black rounded-lg text-sm transition-all active:scale-95 whitespace-nowrap"
                style={{ color: '#0a2342' }}
              >
                <i className="ri-file-list-3-line mr-2" />Get a Free Quote
              </a>
              <a
                href="https://wa.me/8617671262710"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-black rounded-lg text-sm transition-all active:scale-95 whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-whatsapp-line" />WhatsApp Us
              </a>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
